'use client'

import type { JSX, PropsWithChildren } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import type { Placement } from 'react-aria'
import { Focusable } from 'react-aria-components'
import { useDebounceValue, useResizeObserver } from 'usehooks-ts'
import { Tooltip, TooltipTrigger } from '../tooltip/Tooltip.client'

/**
 * TooltipOnCroppedContent
 *
 * Wraps exactly one child element and detects if CSS line-clamp has added an ellipsis ('...').
 * If the text is cropped, displays the child inside a Tooltip showing the full content;
 * otherwise, renders the child with a two-line clamp.
 *
 * Implementation details:
 * - Two refs (ref1, ref2) are used because moving a single ref breaks React’s ref behavior.
 * - Each ref attaches to a cloned child (one for tooltip, one for normal view) to measure
 *   scrollHeight vs. clientHeight and detect cropping.
 * - Container width changes are observed (useResizeObserver) and debounced before re-measuring.
 *
 * Props:
 * @param children  A single React element to wrap.
 * @param tooltipText  Full text to show in the tooltip when content is cropped.
 */

export type TooltipOnCroppedContentProps = UiComponentProps<{
  children: JSX.Element
  tooltipText: string
  placement?: Placement
}>

export function TooltipOnCroppedContent({
  children,
  tooltipText,
  placement = 'top',
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: TooltipOnCroppedContentProps & PropsWithChildren) {
  const rootTestId = dataTestId ?? 'tooltip-on-cropped-content'
  const refContainer = useRef<HTMLDivElement>(null)
  const ref1 = useRef<HTMLDivElement | HTMLAnchorElement | HTMLSpanElement>(null)
  const ref2 = useRef<HTMLDivElement | HTMLAnchorElement | HTMLSpanElement>(null)

  const [showTooltip, setShowTooltip] = useState(false)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const openTooltip = () => setIsTooltipOpen(true)

  const validElements = React.Children.toArray(children).filter((child) =>
    React.isValidElement(child),
  )

  const { width = 0 } = useResizeObserver({
    // @ts-expect-error https://github.com/juliencrn/usehooks-ts/issues/602
    ref: refContainer,
    box: 'border-box',
  })

  const [debounceWidth] = useDebounceValue(width, 500)

  useEffect(() => {
    if (ref1.current) {
      setShowTooltip(ref1.current.scrollHeight > ref1.current.clientHeight)
    }

    if (ref2.current) {
      setShowTooltip(ref2.current.scrollHeight > ref2.current.clientHeight)
    }
  }, [debounceWidth])

  if (validElements.length !== 1 || !validElements[0]) {
    console.error('CroppedTooltip must have exactly one child')

    return children
  }

  const onlyChild = React.Children.only(children)

  // Detect if the child has no line clamp applied
  if (!onlyChild.props.className?.includes('line-clamp')) {
    console.warn(
      'TooltipOnCroppedContent: The child element does not have a line-clamp class applied.',
      'The tooltip will not be displayed.',
    )
  }

  const cloneChild1 = React.cloneElement(onlyChild, {
    ref: ref1,
  })

  const cloneChild2 = React.cloneElement(onlyChild, {
    ref: ref2,
  })

  return (
    <div
      ref={refContainer}
      id={id}
      className={className}
      data-testid={rootTestId}
      aria-label={ariaLabel}
    >
      {showTooltip ? (
        <TooltipTrigger
          isOpen={isTooltipOpen}
          onOpenChange={setIsTooltipOpen}
          delay={0}
          closeDelay={0}
        >
          <Focusable>
            <span
              tabIndex={0}
              onPointerDown={openTooltip}
              onMouseEnter={openTooltip}
              data-testid={`${rootTestId}/trigger`}
            >
              {cloneChild1}
            </span>
          </Focusable>
          <Tooltip placement={placement}>{tooltipText}</Tooltip>
        </TooltipTrigger>
      ) : (
        <div className="line-clamp-2 text-left">{cloneChild2}</div>
      )}
    </div>
  )
}
